import { useRef } from "react";

import usePlayerEngine from "./hooks/usePlayerEngine";
import usePlayerControls from "./hooks/usePlayerControls";
import useAutoplayCountdown from "./hooks/useAutoplayCountdown";
import useCaptions from "./hooks/useCaptions";

import PlayerView from "./view";
import { LaunchDateBanner, PrivateVideoBanner } from "./view/components/Banners";

function isFutureDate(dateString) {
    if (!dateString) return false;
    return new Date(dateString) > new Date();
}

/**
 * Player — controller for the custom content video player.
 *
 * Applies the availability rules (scheduled launch, premium gating), then
 * assembles the media engine, control state and autoplay flow for the view.
 *
 * @param {object}   info        - the lesson/content payload
 * @param {'horizontal'|'vertical'} mode - landscape overlays controls on the
 *                                  video; portrait moves them to a side rail
 * @param {object}   prevContent - previous playlist item, if any
 * @param {object}   nextContent - next playlist item, if any
 * @param {function} onNavigate  - navigate to another playlist content
 * @param {React.ReactNode} [placeholder] - card shown over the stage while the
 *                                  lesson has no video (creators' next actions);
 *                                  the chrome stays visible but inert
 * @param {boolean} [embedded]  - mounted inside a modal rather than on the
 *                                  Content page: no back button, no theater
 *                                  mode, and fullscreen on the player itself
 * @param {boolean} [resume]    - false always starts at zero instead of the
 *                                  position saved on this device
 */
export default function Player({ info, mode = "horizontal", prevContent, nextContent, onNavigate, placeholder = null, embedded = false, resume = true }) {
    const videoRef = useRef(null);
    const rootRef = useRef(null);

    const futureDate = info?.timestamps?.launch || info?.availability?.available_at;
    const isFuture = isFutureDate(futureDate);
    const isAvailable = info?.availability?.available;
    const hasPermissions = ["admin", "owner", "creator"].includes(info?.role);

    // No source yet: every playback control is shown but disabled, so the
    // page keeps the player's footprint and the placeholder reads as part of it.
    const disabled = !info?.video?.src;

    const autoplay = useAutoplayCountdown({
        videoRef,
        contentId: info.id,
        nextContent,
        onNavigate,
    });

    const { watched, toggleWatched, needsPlayGesture, hls } = usePlayerEngine({
        info,
        videoRef,
        onEnded: autoplay.trigger,
        resume,
    });

    const captions = useCaptions({ info, videoRef, hls });

    const controls = usePlayerControls({ videoRef, rootRef, disabled, embedded });

    // Scheduled content: only admins/owners/creators may preview it.
    if (isFuture && !hasPermissions) {
        return (
            <div className={`player ${mode} banner-only`}>
                <div className="player-stage">
                    <LaunchDateBanner content={info} />
                </div>
            </div>
        );
    }

    // Premium content the current user has no access to.
    if (!isFuture && !isAvailable && !hasPermissions && info?.id) {
        return (
            <div className={`player ${mode} banner-only`}>
                <div className="player-stage">
                    <PrivateVideoBanner content={info} community={info?.community} />
                </div>
            </div>
        );
    }

    return (
        <PlayerView
            info={info}
            mode={mode}
            videoRef={videoRef}
            rootRef={rootRef}
            controls={controls}
            captions={captions}
            watched={watched}
            onToggleWatched={toggleWatched}
            prevContent={prevContent}
            nextContent={nextContent}
            onNavigate={onNavigate}
            autoplay={autoplay}
            isAdminPreview={hasPermissions && isFuture}
            needsPlayGesture={needsPlayGesture && !disabled}
            disabled={disabled}
            placeholder={disabled ? placeholder : null}
            embedded={embedded}
        />
    );
}
